import { useWebAppContext } from 'app/src/shared/hooks'
import { replace } from 'lodash'
import { useNavigate, type NavigateOptions, type To } from 'react-router-dom'

export const CatalogPath = '/catalog'
const LandingPath = '/landing'
const UserInfoPath = '/userinfo'
const UserAccountPath = '/useraccount'
const UserJoinPath = '/userjoin'
const ErrorPath = '/error'
const ProposalsPath = '/proposal'
const ShopPath = '/shop'
const ShowcasePath = '/showcase'
const NewsPath = '/news'
const DirectSubPath = 'direct'
const RefundSubPath = 'refund'
const TransferSubPath = 'transfer'
const DiscountSubPath = 'discount'
const TutorialPath = '/tutorial'
const AccessibilityPath = '/accessibility'
const MealVouchersPath = '/mealvouchers'
const DemoExternalOrderPath = '/demoExternalOrder/:orderId'

const AnyPath = '*'
const RootPath = '/'

export enum NavigatePaths {
  Any = AnyPath,
  Root = RootPath,
  Catalog = CatalogPath,
  Landing = LandingPath,
  News = NewsPath,
  NewsDetail = `${NewsPath}/:newsId`,
  Proposals = ProposalsPath,
  UserInfo = UserInfoPath,
  Showcase = ShowcasePath,
  Favourites = `${ShowcasePath}/favourites`,
  UserAccount = UserAccountPath,
  UserJoin = UserJoinPath,
  GiftCard = `${ShopPath}/${DirectSubPath}/giftcard/:id`,
  GoodAndService = `${ShopPath}/${DirectSubPath}/goodandservice/:id`,
  MultiVoucher = `${ShopPath}/${DirectSubPath}/multivoucher/:id`,
  Voucher = `${ShopPath}/${DirectSubPath}/voucher/:id`,
  DiscountDetail = `${ShopPath}/${DirectSubPath}/${DiscountSubPath}/:id`,
  MultiVoucherUsage = `${ShopPath}/${DirectSubPath}/multivoucherusage/:id`,
  MultiVoucherUsageDetail = `${ShopPath}/${DirectSubPath}/multivoucherusagedetail/:id`,
  Refund = `${ShopPath}/${RefundSubPath}/:id`,
  TransferFondoPensione = `${ShopPath}/${TransferSubPath}/fondopensione/:id`,
  TransferFondoSalute = `${ShopPath}/${TransferSubPath}/fondosalute/:id`,
  Tutorial = TutorialPath,
  Discount = `${ShowcasePath}?discount=true`,
  Accessibility = AccessibilityPath,
  Error = ErrorPath,
  MealVouchers = MealVouchersPath,
  DemoExternalOrder = DemoExternalOrderPath,
}

export enum AppBarPaths {
  Catalog = NavigatePaths.Catalog,
  Landing = NavigatePaths.Landing,
  Proposals = NavigatePaths.Proposals,
  UserInfo = NavigatePaths.UserInfo,
  UserAccount = NavigatePaths.UserAccount,
  Tutorial = NavigatePaths.Tutorial,
  Accessibility = NavigatePaths.Accessibility,
  UserJoin = NavigatePaths.UserJoin,
  Showcase = NavigatePaths.Showcase,
  Discount = NavigatePaths.DiscountDetail,
  MealVouchers = NavigatePaths.MealVouchers,
}
export type GotoShowCaseProps = {
  macroCategoryId: string
  serviceCategory: string
  selectedCategory: string
  selectedProduct: string
  breadCrumb: string
  favourites: boolean
  discount: boolean
}
export const useWelfarePortalNavigate = () => {
  const navigate = useNavigate()
  const context = useWebAppContext()
  const navigateDelegate = (path: To, options?: NavigateOptions) => {
    navigate(path, options)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const goToCatalog = () => navigateDelegate(NavigatePaths.Catalog)
  const goToLanding = () => navigateDelegate(NavigatePaths.Landing)
  const goToNews = () => navigateDelegate(NavigatePaths.News)
  const goToUserJoin = () => navigateDelegate(NavigatePaths.UserJoin)
  const goToNewsDetail = (newsId: string) => navigateDelegate(replace(NavigatePaths.NewsDetail, ':newsId', newsId))
  const goToProposals = () => navigateDelegate(NavigatePaths.Proposals)
  const goToShowcase = (props: Partial<GotoShowCaseProps>) => {
    let queryString = ''

    if (props.macroCategoryId) queryString += `&macroCategoryId=${props.macroCategoryId}`
    if (props.serviceCategory) queryString += `&serviceCategory=${props.serviceCategory}`
    if (props.selectedCategory) queryString += `&selectedCategory=${props.selectedCategory}`
    if (props.breadCrumb) queryString += `&breadcrumb=${props.breadCrumb}`
    if (props.selectedProduct) queryString += `&selectedProduct=${props.selectedProduct}`
    if (props.favourites) queryString += `&favourites=true`
    console.log('goToShowcase queryString', queryString)
    if (queryString) queryString = '?' + queryString.slice(1)
    navigateDelegate(NavigatePaths.Showcase + queryString)
  }
  const goToUserInfo = () => {
    navigateDelegate(NavigatePaths.UserInfo)
    context.update({ showUserSidePanel: false })
  }
  const goToUserAccount = () => {
    navigateDelegate(NavigatePaths.UserAccount)
    context.update({ showUserSidePanel: false })
  }

  const goToTutorial = () => {
    navigateDelegate(NavigatePaths.Tutorial)
    context.update({ showUserSidePanel: false })
  }

  const goToAccessibility = () => {
    navigateDelegate(NavigatePaths.Accessibility)
    context.update({ showUserSidePanel: false })
  }

  const goToFavourites = () => {
    navigateDelegate(NavigatePaths.Favourites)
    context.update({ showUserSidePanel: false })
  }

  const goToDiscount = () => {
    goToShowcase({ macroCategoryId: 'DISCOUNT' })
  }

  const goToMealVouchers = () => {
    navigateDelegate(NavigatePaths.MealVouchers)
  }

  const isActivePath = (appBarPath: AppBarPaths, pathname: string, search?: string) => {
    let matchFound = false

    const discount = search?.includes('macroCategoryId=DISCOUNT') || pathname.includes('/discount/')

    if (appBarPath === pathname) return true

    switch (appBarPath) {
      case AppBarPaths.UserAccount:
        if (pathname.startsWith(UserAccountPath)) return true
        break
      case AppBarPaths.Discount:
        if (!pathname.startsWith(UserAccountPath) && (discount || (pathname.startsWith(ShowcasePath) && discount))) return true
        break
      case AppBarPaths.Showcase:
        if (pathname.startsWith(ShowcasePath) && !discount) return true
        break
      case AppBarPaths.Catalog:
        if (
          (pathname.startsWith(ShopPath) && !pathname.includes('/discount/')) ||
          (pathname.startsWith(ShowcasePath) && !discount) ||
          pathname.startsWith(ProposalsPath)
        )
          return true
        break
      case AppBarPaths.Landing:
        if (pathname.startsWith(NewsPath)) return true

        Object.values(AppBarPaths).forEach((val) => {
          if (val !== AppBarPaths.Landing && !matchFound) matchFound = isActivePath(val, pathname)
        })
        return !matchFound
      default:
        break
    }
    return false
  }

  return {
    isActivePath,
    goToCatalog,
    goToLanding,
    goToNews,
    goToNewsDetail,
    goToProposals,
    goToUserInfo,
    goToShowcase,
    goToUserAccount,
    goToTutorial,
    goToAccessibility,
    goToUserJoin,
    goToFavourites,
    goToDiscount,
    navigateDelegate,
    goToMealVouchers,
  }
}
